import React, {useEffect, useRef, useState} from "react";
import {
    AREA_STATUS_READONLY,
    Area,
    BLE_SCAN_MODE_NONE,
    resolveAreaAutoPlay,
    Spot,
    SpotType,
    Version,
    Zone
} from "../types";
import {useMutation} from "@apollo/client";
import {
    createPolygonSpotMutation,
    createSpotMutation,
    deleteSpotMutation,
    updateMarkerSpotMutation,
    updatePolygonSpotMutation
} from "../spots/graphQl";
import {Box, useToast} from "@chakra-ui/react";
import {AlertStatus} from "@chakra-ui/alert";
import {updateAreaContentMutation} from "../area/areaQueries";
import {computeAreaIosCompatibility} from "../area/iosCompatibility";
import moment from "moment";

export type AppContextI = {
    getSpots: () => Spot[],
    setSpots: (spots: Spot[]) => void,
    setSelection: (selectedSpot: Spot | undefined) => void,
    getSelection: () => Spot | undefined,
    updateSpot: (spot?: Spot, newSpot?: Spot) => Promise<Spot | undefined> | undefined;
    deleteSelectedSpot: (spotToDelete: Spot) => void;
    createSpot: (newSpot: Spot, options?: {select?: boolean; suppressLocalInsert?: boolean}) => Promise<Spot | undefined> | undefined;

    setSelectedSpotId: (id: string) => void;
    isSelected: (spot: Spot) => boolean;

    getArea: () => Area | undefined
    setArea: (area: Area) => void

    setZoneSelection: (zone: Zone | undefined) => void;

    getZoneSelection: () => Zone | undefined;
    updateArea: (area?: Area, newArea?: Area) => void;
    updateZone: (zone?: Zone, newZone?: Zone) => void;

    showError: (message: string) => void

    getSpot: (copyFromSpotId: string | undefined) => Spot | undefined;

    setDisplayedVersion: (version: Version) => void;
    getDisplayedVersion: () => Version | undefined;

    getModifiedAt: () => number;
    notifyContentChanged: () => void;

    isSaveStatusIdle: () => boolean;
    isSaveStatusSaving: () => boolean;
    isSaveStatusSaved: () => boolean;
    trackSavePromise: <T>(operation: Promise<T>) => Promise<T>;

    isBleEnabled: () => boolean;
    isOnDemand: () => boolean;
    isReadOnly: () => boolean;
    isDraft: () => boolean;
    getOrganizationAppVersion: () => string | undefined;
    setOrganizationAppVersion: (version: string | undefined) => void;
}

export const AppContext = React.createContext<AppContextI>(
    {
        setSelection: () => {
        },
        getSelection: () => undefined,
        getSpots: () => [],
        setSpots: () => null,
        updateSpot: () => undefined,
        deleteSelectedSpot: () => null,
        createSpot: () => undefined,

        setSelectedSpotId: () => null,
        isSelected: () => false,

        getArea: () => undefined,
        setArea: () => null,

        setZoneSelection: () => null,
        getZoneSelection: () => undefined,
        updateZone: () => null,
        updateArea: () => null,

        getSpot: () => undefined,

        showError: () => null,

        setDisplayedVersion: () => null,
        getDisplayedVersion: () => undefined,

        getModifiedAt: () => 0,
        notifyContentChanged: () => {},
        isSaveStatusIdle: () => true,
        isSaveStatusSaving: () => false,
        isSaveStatusSaved: () => false,
        trackSavePromise: <T,>(operation: Promise<T>) => operation,

        isBleEnabled: () => true,
        isOnDemand: () => false,
        isReadOnly: () => false,
        isDraft: () => false,
        getOrganizationAppVersion: () => undefined,
        setOrganizationAppVersion: () => {}
    }
);

type Props = {
    children: any
}

const SAVE_STATUS_IDLE = "idle";
const SAVE_STATUS_SAVING = "saving";
const SAVE_STATUS_SAVED = "saved";
type SaveStatus = typeof SAVE_STATUS_IDLE | typeof SAVE_STATUS_SAVING | typeof SAVE_STATUS_SAVED;

const ContextWrapper = (props: Props) => {
    const toast = useToast();
    const [updateAreaContent] = useMutation(updateAreaContentMutation);
    const [createMarkerSpot] = useMutation(createSpotMutation);
    const [createPolygonSpot] = useMutation(createPolygonSpotMutation);
    const [updateMarkerSpot] = useMutation(updateMarkerSpotMutation);
    const [updatePolygonSpot] = useMutation(updatePolygonSpotMutation);
    const [deleteSpot] = useMutation(deleteSpotMutation);
    const [spots, setSpots] = useState<Spot[]>([]);
    const spotsRef = useRef<Spot[]>([]);
    const [spotsLoadedForAreaId, setSpotsLoadedForAreaId] = useState<string | undefined>(undefined);
    const [area, setArea] = useState<Area | undefined>(undefined);
    const areaRef = useRef<Area | undefined>(undefined);
    const [selectedSpot, setSelectedSpot] = useState<Spot | undefined>(undefined);
    const [selectedZone, setSelectedZone] = useState<Zone | undefined>(undefined);
    const selectedSpotIdRef = useRef<string | undefined>(undefined);

    const [organizationAppVersion, setOrganizationAppVersion] = useState<string | undefined>(undefined);
    const [activeVersion, setActiveVersion] = useState<Version | undefined>(undefined);
    const [modifiedAt, setModifiedAt] = useState<number>(0);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>(SAVE_STATUS_IDLE);
    const pendingSaveCountRef = useRef<number>(0);
    const saveStatusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestSpotMutationSeqRef = useRef<Record<string, number>>({});
    const latestSpotServerModifiedAtRef = useRef<Record<string, number | undefined>>({});
    const spotMutationChainRef = useRef<Record<string, Promise<Spot | undefined>>>({});
    const latestAreaMutationSeqRef = useRef<number>(0);
    const latestAreaServerModifiedAtRef = useRef<number | undefined>(undefined);
    const areaMutationChainRef = useRef<Promise<Area | undefined> | undefined>(undefined);

    useEffect(() => {
        spotsRef.current = spots;
        spots.forEach((spot) => {
            latestSpotServerModifiedAtRef.current[spot.id] = spot.modifiedAt;
        });
    }, [spots]);

    useEffect(() => {
        areaRef.current = area;
        if (typeof area?.modifiedAt === "number") {
            latestAreaServerModifiedAtRef.current = area.modifiedAt;
        }
        console.info("[area-state] updated", {
            areaId: area?.id,
            modifiedAt: area?.modifiedAt,
            latestServerModifiedAt: latestAreaServerModifiedAtRef.current,
            name: area?.name,
            bleScanMode: area?.bleScanMode,
            groupMode: area?.groupMode
        });
    }, [area]);

    useEffect(() => {
        return () => {
            if (saveStatusTimerRef.current) {
                clearTimeout(saveStatusTimerRef.current);
                saveStatusTimerRef.current = null;
            }
        };
    }, []);

    const clearSaveStatusTimer = () => {
        if (saveStatusTimerRef.current) {
            clearTimeout(saveStatusTimerRef.current);
            saveStatusTimerRef.current = null;
        }
    };

    const beginSave = () => {
        pendingSaveCountRef.current += 1;
        clearSaveStatusTimer();
        setSaveStatus(SAVE_STATUS_SAVING);
    };

    const completeSave = (isSuccess: boolean) => {
        pendingSaveCountRef.current = Math.max(0, pendingSaveCountRef.current - 1);
        if (pendingSaveCountRef.current > 0) {
            return;
        }
        clearSaveStatusTimer();
        if (!isSuccess) {
            setSaveStatus(SAVE_STATUS_IDLE);
            return;
        }
        setSaveStatus(SAVE_STATUS_SAVED);
        saveStatusTimerRef.current = setTimeout(() => {
            setSaveStatus(SAVE_STATUS_IDLE);
            saveStatusTimerRef.current = null;
        }, 2000);
    };

    const showToast = (text: string, status: AlertStatus = "info") => {
        toast({
            duration: 800,
            variant: status === "info" ? "subtle" : "solid",
            status: status,
            position: 'top-right',
            render: () => (
                <Box color='black' p={3} bg='blue.100' width={"200px"} textAlign={"center"}>
                    {text}
                </Box>
            ),
        })
    }

    const isConflictError = (error: unknown): boolean => {
        if (error instanceof Error) {
            return error.message.includes("CONFLICT:");
        }
        if (typeof error === "object" && error !== null && "graphQLErrors" in error) {
            const gqlErrors = (error as any).graphQLErrors;
            return Array.isArray(gqlErrors) && gqlErrors.some((e: any) => e?.message?.includes("CONFLICT:"));
        }
        return false;
    };

    const showConflictToast = (entityLabel: string) => {
        toast({
            title: "Save conflict",
            description: `This ${entityLabel} was modified by another user. Your changes were not saved. Please reload the page to get the latest version.`,
            status: "warning",
            duration: null,
            isClosable: true,
            position: "top",
        });
    };

    const applySelectedSpot = (nextSelectedSpot: Spot | undefined) => {
        selectedSpotIdRef.current = nextSelectedSpot?.id;
        setSelectedSpot(nextSelectedSpot);
    };

    const setSelectedSpotId = (id: string) => {
        const spot = spots.find((spot: Spot) => spot.id === id);
        applySelectedSpot(spot);
    }

    const isDraft = () => {
        return (activeVersion === undefined || !activeVersion.publishedAt)
            && area?.status !== AREA_STATUS_READONLY;
    }

    const isSaveStatusIdle = () => saveStatus === SAVE_STATUS_IDLE;
    const isSaveStatusSaving = () => saveStatus === SAVE_STATUS_SAVING;
    const isSaveStatusSaved = () => saveStatus === SAVE_STATUS_SAVED;

    const isBleEnabled = () => {
        return area?.bleScanMode !== BLE_SCAN_MODE_NONE;
    };

    const isOnDemand = () => {
        return !resolveAreaAutoPlay(area);
    };

    const isReadOnly = () => {
        return area?.status === AREA_STATUS_READONLY;
    };

    const trackSavePromise = <T,>(operation: Promise<T>): Promise<T> => {
        beginSave();
        return operation
            .then((result) => {
                completeSave(true);
                return result;
            })
            .catch((error) => {
                completeSave(false);
                throw error;
            });
    };

    const mergeSpotUpdate = (baseSpot: Spot, incomingSpot: Spot): Spot => {
        return {
            ...baseSpot,
            ...incomingSpot,
            soundFiles: incomingSpot.soundFiles ?? baseSpot.soundFiles,
            beaconTriggers: incomingSpot.beaconTriggers ?? baseSpot.beaconTriggers,
            wiFiTriggers: incomingSpot.wiFiTriggers ?? baseSpot.wiFiTriggers,
            gpsTriggers: incomingSpot.gpsTriggers ?? baseSpot.gpsTriggers,
            triggerRules: incomingSpot.triggerRules ?? baseSpot.triggerRules
        };
    };

    const mergeAreaUpdate = (baseArea: Area, incomingArea: Area): Area => {
        return {
            ...baseArea,
            ...incomingArea,
            zones: incomingArea.zones ?? baseArea.zones,
            languages: incomingArea.languages ?? baseArea.languages,
            defaults: incomingArea.defaults ?? baseArea.defaults,
            groupMode: incomingArea.groupMode ?? baseArea.groupMode
        };
    };

    const getChangedAreaKeys = (baseArea: Area, incomingArea: Area): string[] => {
        return Object.keys(incomingArea).filter((key) => {
            const typedKey = key as keyof Area;
            return incomingArea[typedKey] !== baseArea[typedKey];
        });
    };

    const queueAreaMutation = (incomingArea: Area, source: string) => {
        const nextMutationSeq = latestAreaMutationSeqRef.current + 1;
        latestAreaMutationSeqRef.current = nextMutationSeq;
        const optimisticBaseArea = areaRef.current || incomingArea;
        const optimisticMergedArea = mergeAreaUpdate(optimisticBaseArea, incomingArea);
        const changedKeys = getChangedAreaKeys(optimisticBaseArea, incomingArea);
        areaRef.current = optimisticMergedArea;
        setArea(optimisticMergedArea);

        console.info("[area-save] queued", {
            seq: nextMutationSeq,
            source: source,
            areaId: incomingArea.id,
            localModifiedAt: optimisticBaseArea.modifiedAt,
            latestServerModifiedAt: latestAreaServerModifiedAtRef.current,
            changedKeys: changedKeys,
        });

        beginSave();
        const runMutation = () => {
            const currentArea = areaRef.current || optimisticMergedArea;
            const mergedArea = mergeAreaUpdate(currentArea, incomingArea);
            const expectedModifiedAt = latestAreaServerModifiedAtRef.current ?? currentArea.modifiedAt;
            const newAreaVars = {
                ...mergedArea,
                areaId: mergedArea.id,
                expectedModifiedAt: expectedModifiedAt,
            };

            console.info("[area-save] request", {
                seq: nextMutationSeq,
                source: source,
                areaId: mergedArea.id,
                expectedModifiedAt: expectedModifiedAt,
                currentAreaModifiedAt: currentArea.modifiedAt,
                incomingAreaModifiedAt: incomingArea.modifiedAt,
                latestServerModifiedAt: latestAreaServerModifiedAtRef.current,
                changedKeys: changedKeys,
            });

            return updateAreaContent({
                variables: newAreaVars
            })
                .then((result) => {
                    const serverArea = result.data?.updateAreaContent;
                    const finalizedArea = serverArea
                        ? {...mergeAreaUpdate(serverArea, mergedArea), modifiedAt: serverArea.modifiedAt}
                        : mergedArea;
                    console.info("[area-save] response", {
                        seq: nextMutationSeq,
                        source: source,
                        areaId: mergedArea.id,
                        expectedModifiedAt: expectedModifiedAt,
                        serverModifiedAt: serverArea?.modifiedAt,
                        finalizedModifiedAt: finalizedArea.modifiedAt,
                        changedKeys: changedKeys,
                    });
                    if (typeof finalizedArea.modifiedAt === "number") {
                        latestAreaServerModifiedAtRef.current = finalizedArea.modifiedAt;
                    }
                    const isLatestMutation = latestAreaMutationSeqRef.current === nextMutationSeq;
                    if (isLatestMutation) {
                        areaRef.current = finalizedArea;
                        setArea(finalizedArea);
                    }
                    setModifiedAt(moment().unix());
                    completeSave(true);
                    return finalizedArea;
                })
                .catch((error) => {
                    const isLatestMutation = latestAreaMutationSeqRef.current === nextMutationSeq;
                    if (isLatestMutation) {
                        areaRef.current = currentArea;
                        setArea(currentArea);
                    }
                    console.warn("[area-save] error", {
                        seq: nextMutationSeq,
                        source: source,
                        areaId: mergedArea.id,
                        expectedModifiedAt: expectedModifiedAt,
                        currentAreaModifiedAt: currentArea.modifiedAt,
                        latestServerModifiedAt: latestAreaServerModifiedAtRef.current,
                        changedKeys: changedKeys,
                        message: error instanceof Error ? error.message : error,
                    });
                    console.error(error);
                    completeSave(false);
                    if (isConflictError(error)) {
                        showConflictToast("space");
                    } else {
                        showToast("Unable to save space changes", "error");
                    }
                    return undefined;
                });
        };

        const previousMutation = areaMutationChainRef.current || Promise.resolve(undefined);
        const queuedMutation = previousMutation
            .catch(() => undefined)
            .then(() => runMutation());
        const chainMutation = queuedMutation.finally(() => {
            if (areaMutationChainRef.current === chainMutation) {
                areaMutationChainRef.current = undefined;
            }
        });
        areaMutationChainRef.current = chainMutation;
        return queuedMutation;
    };

    useEffect(() => {
        if (!area || !isDraft()) {
            return;
        }
        if (spotsLoadedForAreaId !== area.id) {
            return;
        }
        const computed = computeAreaIosCompatibility(area, spots);
        if (area.iosCompatible !== computed) {
            queueAreaMutation({...area, iosCompatible: computed}, "iosCompatibility:sweep");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spots, area?.id, area?.zones, area?.iosCompatible, spotsLoadedForAreaId]);

    return <AppContext.Provider value={
        {
            getSpots: () => spots,
            getSpot: (spotId: string) => spots.find(spot => spot.id === spotId),
            setSpots: (spots: Spot[]) => {
                setSpots(spots);
                const areaId = areaRef.current?.id;
                if (areaId) {
                    setSpotsLoadedForAreaId(areaId);
                }
                const selectedId = selectedSpotIdRef.current;
                if (selectedId) {
                    const updatedSelection = spots.find((spot: Spot) => spot.id === selectedId);
                    applySelectedSpot(updatedSelection);
                }
            },
            setSelection: (selectedSpot: Spot | undefined) => {
                applySelectedSpot(selectedSpot);
            },
            getSelection: () => selectedSpot,

            updateSpot: (spot?: Spot, newSpot?: Spot) => {
                if (spot && newSpot && isDraft()) {
                    const nextMutationSeq = (latestSpotMutationSeqRef.current[spot.id] || 0) + 1;
                    latestSpotMutationSeqRef.current[spot.id] = nextMutationSeq;
                    const optimisticBaseSpot = spotsRef.current.find((existingSpot) => existingSpot.id === spot.id) || spot;
                    const optimisticMergedSpot: Spot = mergeSpotUpdate(optimisticBaseSpot, newSpot);
                    Object.keys(newSpot).filter((key) => {
                        const typedKey = key as keyof Spot;
                        return newSpot[typedKey] !== optimisticBaseSpot[typedKey];
                    });
                    setSpots((prevSpots) => {
                        const list = prevSpots ? [...prevSpots] : [];
                        const index = list.findIndex((existingSpot: Spot) => existingSpot.id === spot.id);
                        if (index >= 0) {
                            list[index] = optimisticMergedSpot;
                        } else {
                            list.push(optimisticMergedSpot);
                        }
                        return list;
                    });
                    if (selectedSpotIdRef.current === spot.id) {
                        applySelectedSpot(optimisticMergedSpot);
                    }

                    beginSave();
                    const runMutation = () => {
                        const currentSpot = spotsRef.current.find((existingSpot) => existingSpot.id === spot.id) || optimisticMergedSpot;
                        const mergedSpot: Spot = mergeSpotUpdate(currentSpot, newSpot);
                        const mutation = mergedSpot.type === SpotType.Polygon ? updatePolygonSpot : updateMarkerSpot;
                        const expectedModifiedAt = latestSpotServerModifiedAtRef.current[spot.id] ?? currentSpot.modifiedAt;
                        return mutation({
                            variables: {
                                ...mergedSpot,
                                expectedModifiedAt: expectedModifiedAt
                            }
                        })
                        .then((result) => {
                            const mutationResultSpot = result.data?.updateMarkerSpot || result.data?.updatePolygonSpot;
                            // Keep local requested changes authoritative if the backend responds with stale fields,
                            // but always use the server's modifiedAt for optimistic concurrency.
                            const finalizedSpot = mutationResultSpot
                                ? {...mergeSpotUpdate(mutationResultSpot, mergedSpot), modifiedAt: mutationResultSpot.modifiedAt}
                                : mergedSpot;
                            if (typeof finalizedSpot.modifiedAt === "number") {
                                latestSpotServerModifiedAtRef.current[spot.id] = finalizedSpot.modifiedAt;
                            }
                            const isLatestMutation = latestSpotMutationSeqRef.current[spot.id] === nextMutationSeq;
                            if (isLatestMutation) {
                                setSpots((prevSpots) => {
                                    const list = prevSpots ? [...prevSpots] : [];
                                    const index = list.findIndex((existingSpot: Spot) => spot.id === existingSpot.id);
                                    if (index >= 0) {
                                        list[index] = finalizedSpot;
                                    } else {
                                        list.push(finalizedSpot);
                                    }
                                    return list;
                                });
                                if (selectedSpotIdRef.current === spot.id) {
                                    applySelectedSpot(finalizedSpot);
                                }
                            }

                            setModifiedAt(moment().unix());
                            completeSave(true);
                            return finalizedSpot;
                        })
                        .catch((error) => {
                            const isLatestMutation = latestSpotMutationSeqRef.current[spot.id] === nextMutationSeq;
                            if (isLatestMutation) {
                                setSpots((prevSpots) => {
                                    const list = prevSpots ? [...prevSpots] : [];
                                    const index = list.findIndex((existingSpot: Spot) => existingSpot.id === spot.id);
                                    if (index >= 0) {
                                        list[index] = currentSpot;
                                    } else {
                                        list.push(currentSpot);
                                    }
                                    return list;
                                });
                                if (selectedSpotIdRef.current === spot.id) {
                                    applySelectedSpot(currentSpot);
                                }
                            }
                            console.error(error);
                            completeSave(false);
                            if (isConflictError(error)) {
                                showConflictToast("spot");
                            } else {
                                showToast("Unable to save spot changes", "error");
                            }
                            return undefined;
                        });
                    };
                    const previousMutation = spotMutationChainRef.current[spot.id] || Promise.resolve(undefined);
                    const queuedMutation = previousMutation
                        .catch(() => undefined)
                        .then(() => runMutation());
                    const chainMutation = queuedMutation.finally(() => {
                        if (spotMutationChainRef.current[spot.id] === chainMutation) {
                            delete spotMutationChainRef.current[spot.id];
                        }
                    });
                    spotMutationChainRef.current[spot.id] = chainMutation;
                    return queuedMutation;
                }
                return undefined;
            },

            createSpot: (newSpot: Spot, options?: {select?: boolean; suppressLocalInsert?: boolean}) => {
                if (isDraft()) {
                    const shouldSelect = options?.select !== false;
                    const suppressLocalInsert = options?.suppressLocalInsert === true;
                    const baseVariables = {
                        ...newSpot,
                        areaId: area?.id
                    };
                    const mutation = newSpot.type === SpotType.Polygon ? createPolygonSpot : createMarkerSpot;
                    beginSave();
                    return mutation({
                        variables: baseVariables
                    })
                        .then((result) => {
                            const newSpotData = result.data?.createMarkerSpot || result.data?.createPolygonSpot;
                            newSpot = {
                                ...newSpotData
                            }
                            if (!suppressLocalInsert) {
                                setSpots((prevSpots: Spot[] | undefined) => {
                                    const list = prevSpots || [];
                                    return [...list, newSpot];
                                });
                            }
                            if (shouldSelect) {
                                applySelectedSpot(newSpot);
                            }

                            setModifiedAt(moment().unix());
                            completeSave(true);
                            return newSpot;
                        })
                        .catch((error) => {
                            console.error(error);
                            completeSave(false);
                            showToast("Unable to create spot", "error");
                            return undefined;
                        })
                }
                return undefined;
            },
            deleteSelectedSpot: (spotToDelete: Spot) => {
                if (!(isDraft() && spotToDelete)) {
                    return;
                }
                beginSave();
                deleteSpot({
                    variables: {
                        id: spotToDelete.id
                    }
                })
                    .then(() => {
                        applySelectedSpot(undefined);
                        setSpots((previousSpots: Spot[] | undefined) => {
                            const current = previousSpots || [];
                            return current.filter((spot) => spot.id !== spotToDelete.id);
                        });

                        setModifiedAt(moment().unix());
                        completeSave(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        completeSave(false);
                        showToast("Unable to delete spot", "error");
                    });
            },
            setSelectedSpotId: setSelectedSpotId,
            isSelected: (spot: Spot) => {
                return spot !== undefined
                    && selectedSpot !== undefined
                    && spot.id === selectedSpot.id;
            },
            getArea: () => area,
            setArea: setArea,

            setZoneSelection: setSelectedZone,
            getZoneSelection: () => selectedZone,
            updateZone: (zone?: Zone, newZone?: Zone) => {
                const currentArea = areaRef.current;
                if (zone && newZone && currentArea && isDraft()) {
                    let nextZones = {
                        ...currentArea.zones
                    };
                    if (Array.isArray(nextZones.ambientZones)) {
                        const ambientIndex = nextZones.ambientZones.findIndex((item) => item?.id === zone.id);
                        if (ambientIndex > -1) {
                            const updatedAmbient = [...nextZones.ambientZones];
                            updatedAmbient[ambientIndex] = newZone;
                            nextZones = {
                                ...nextZones,
                                ambientZones: updatedAmbient
                            };
                        }
                    }
                    if (!Array.isArray(nextZones.ambientZones) || !nextZones.ambientZones.some((item) => item?.id === zone.id)) {
                        const newZoneDef: any = {};
                        newZoneDef[newZone.id] = newZone; // wrap it in its id
                        nextZones = {
                            ...nextZones,
                            ...newZoneDef
                        };
                    }
                    let newArea = {
                        ...currentArea,
                        id: currentArea.id,
                        zones: {
                            ...nextZones
                        }
                    };
                    queueAreaMutation(newArea, `updateZone:${zone.id}`);
                }
            },

            updateArea: (area?: Area, newArea?: Area) => {
                if ((areaRef.current || area) && newArea && isDraft()) {
                    queueAreaMutation(newArea, "updateArea");
                }
            },

            showError: (message: string) => showToast(message, "error"),

            setDisplayedVersion: setActiveVersion,
            getDisplayedVersion: () => activeVersion,
            getModifiedAt: () => modifiedAt,
            notifyContentChanged: () => setModifiedAt(moment().unix()),
            isSaveStatusIdle: isSaveStatusIdle,
            isSaveStatusSaving: isSaveStatusSaving,
            isSaveStatusSaved: isSaveStatusSaved,
            trackSavePromise: trackSavePromise,
            isBleEnabled: isBleEnabled,
            isOnDemand: isOnDemand,
            isReadOnly: isReadOnly,
            isDraft: isDraft,
            getOrganizationAppVersion: () => organizationAppVersion,
            setOrganizationAppVersion: setOrganizationAppVersion
        }
    }>
        {props.children}
    </AppContext.Provider>
}

export default ContextWrapper
